import { isObject } from '../../typescript/is-object.js';
import { emitSectionEvent } from '../events/index.js';
const sectionActions = new WeakMap();
const sectionSelectCleanupFns = new WeakMap();
const blockSelectCleanupFns = new WeakMap();
const parseSection = (evt, selectors)=>{
    if (evt.target == null) {
        return null;
    }
    const sectionId = 'detail' in evt && isObject(evt.detail) && 'sectionId' in evt.detail && typeof evt.detail.sectionId === 'string' && evt.detail.sectionId.length > 0 ? evt.detail.sectionId : null;
    if (sectionId == null) {
        return null;
    }
    return evt.target instanceof HTMLElement && evt.target.getAttribute('id') === 'shopify-section-' + sectionId && evt.target.matches(selectors) ? evt.target : null;
};
const parseBlock = (evt)=>{
    const blockId = 'detail' in evt && isObject(evt.detail) && 'blockId' in evt.detail && typeof evt.detail.blockId === 'string' && evt.detail.blockId.length > 0 ? evt.detail.blockId : null;
    if (blockId == null) {
        return null;
    }
    const _shopifyAttrs = evt.target instanceof HTMLElement && evt.target.getAttribute('data-shopify-editor-block') || null;
    if (_shopifyAttrs == null) {
        return null;
    }
    try {
        const parsedShopifyAttrs = JSON.parse(_shopifyAttrs);
        const id = isObject(parsedShopifyAttrs) && 'id' in parsedShopifyAttrs && typeof parsedShopifyAttrs.id === 'string' && parsedShopifyAttrs.id.length > 0 ? parsedShopifyAttrs.id : null;
        return evt.target instanceof HTMLElement && id === blockId ? evt.target : null;
    } catch (err) {
        console.warn('Shopify attributes cannot be parsed', {
            blockId,
            shopifyAttributes: _shopifyAttrs
        }, err);
        return null;
    }
};
const parseBlockSection = (block, selectors)=>{
    if (block == null) {
        return null;
    }
    const _section = block.closest('.shopify-section');
    const section = _section instanceof HTMLElement && _section.matches(selectors) ? _section : null;
    return section;
};
/**
 * @param selectors
 *   A CSS Selectors string to distinguish your section, e.g., `.js-slideshow`
 * @param initFn
 *   The initialization function (`load`) that accepts the section's HTML Element, initializes the code related to the section, and returns a set of action functions: `unload`, `select`, `selectBlock`, and `reorder`
 * @returns
 *   Clean-up function to destroy all attached event listeners related to the section
 */ export const initSection = (selectors, initFn)=>{
    const onSectionLoad = (evt)=>{
        const section = parseSection(evt, selectors);
        if (section == null) {
            return;
        }
        const actions = initFn(section);
        sectionActions.set(section, actions);
    };
    const onSectionUnload = (evt)=>{
        const section = parseSection(evt, selectors);
        if (section == null) {
            return;
        }
        const actions = sectionActions.get(section);
        if (actions == null || actions.unload == null) {
            return;
        }
        actions.unload();
    };
    const onSectionSelect = (evt)=>{
        const section = parseSection(evt, selectors);
        if (section == null) {
            return;
        }
        const actions = sectionActions.get(section);
        if (actions == null || actions.select == null) {
            return;
        }
        const cleanup = actions.select();
        if (cleanup == null) {
            return;
        }
        sectionSelectCleanupFns.set(section, cleanup);
    };
    const onSectionDeselect = (evt)=>{
        const section = parseSection(evt, selectors);
        if (section == null) {
            return;
        }
        const cleanup = sectionSelectCleanupFns.get(section);
        if (cleanup == null) {
            return;
        }
        cleanup();
    };
    const onSectioReorder = (evt)=>{
        const section = parseSection(evt, selectors);
        if (section == null) {
            return;
        }
        const actions = sectionActions.get(section);
        if (actions == null || actions.reorder == null) {
            return;
        }
        actions.reorder();
    };
    const onBlockSelect = (evt)=>{
        const block = parseBlock(evt);
        if (block == null) {
            return;
        }
        const section = parseBlockSection(block, selectors);
        if (section == null) {
            return;
        }
        const actions = sectionActions.get(section);
        if (actions == null || actions.selectBlock == null) {
            return;
        }
        const cleanup = actions.selectBlock(block);
        if (cleanup == null) {
            return;
        }
        blockSelectCleanupFns.set(block, cleanup);
    };
    const onBlockDeselect = (evt)=>{
        const block = parseBlock(evt);
        if (block == null) {
            return;
        }
        const cleanup = blockSelectCleanupFns.get(block);
        if (cleanup == null) {
            return;
        }
        cleanup();
    };
    document.addEventListener('shopify:section:load', onSectionLoad);
    document.addEventListener('shopify:section:unload', onSectionUnload);
    document.addEventListener('shopify:section:select', onSectionSelect);
    document.addEventListener('shopify:section:deselect', onSectionDeselect);
    document.addEventListener('shopify:block:select', onBlockSelect);
    document.addEventListener('shopify:block:deselect', onBlockDeselect);
    document.addEventListener('shopify:section:reorder', onSectioReorder);
    /**
   * Init matching sections on initial page load outside of Theme Editor
   */ document.querySelectorAll(selectors).forEach((section)=>{
        if (section instanceof HTMLElement && section.matches('.shopify-section')) {
            var _section_getAttribute;
            const sectionId = ((_section_getAttribute = section.getAttribute('id')) === null || _section_getAttribute === void 0 ? void 0 : _section_getAttribute.replace('shopify-section-', '')) || null;
            if (sectionId == null) {
                return;
            }
            emitSectionEvent(section, {
                type: 'shopify:section:load',
                detail: {
                    sectionId
                }
            });
        }
    });
    return ()=>{
        document.removeEventListener('shopify:section:load', onSectionLoad);
        document.removeEventListener('shopify:section:unload', onSectionUnload);
        document.removeEventListener('shopify:section:select', onSectionSelect);
        document.removeEventListener('shopify:section:deselect', onSectionDeselect);
        document.removeEventListener('shopify:block:select', onBlockSelect);
        document.removeEventListener('shopify:block:deselect', onBlockDeselect);
    };
};
